import { useState } from 'react';
import { Size, t } from '@bms2/shared';
import { AlertBanner, DatePicker, Select, TextInput } from '../../components/controls';
import { Modal } from '../../components/modals/Modal';
import modalStyles from '../../components/modals/modal.module.css';
import { useDocumentToday } from '../../shell/ViewerZone';
import { post, useApi } from './api';

/** The active levels Set level offers, and the write it posts to; both are the org chart's own routes (T726; decision 1495). */
export const ORG_LEVELS_PATH = '/org/levels';
export const ORG_SET_LEVEL_PATH = '/org/set-level';

/** The row the pencil was pressed on: the employment, the person's name, and the level its open position holds. */
export interface SetLevelEdit {
  employmentId: string;
  /** The company the row names, shown under the title so the window says whose position it is. */
  label: string;
  skillLevelId: string | null;
  skillLevelName: string | null;
}

/**
 * Set level (T726; decision 1495), the pencil on the Person page's Employments
 * row for a position at our own Company.
 *
 * 🔒 It offers No Level and the active levels lowest first, opens on the level
 * the position holds, and saves through the org chart's own service: the new
 * dated position a move writes, carrying the level, with the manager and the
 * department kept. A level change is therefore dated and carries a reason, as
 * the Move window's is, and the org chart's window stays.
 *
 * 🔨 A retired level the position still holds is offered, so opening the window
 * changes nothing by itself (decision 1123).
 */
export function SetLevelModal({ editing, apiBaseUrl = '', onCancel, onSaved }: {
  editing: SetLevelEdit;
  apiBaseUrl?: string;
  onCancel: () => void;
  onSaved: () => void;
}) {
  const today = useDocumentToday();
  const levels = useApi<{ rows: { id: string; name: string; level: number }[] }>(ORG_LEVELS_PATH, apiBaseUrl);
  const active = levels.state.kind === 'ready' ? levels.state.data.rows : [];
  const [skillLevelId, setSkillLevelId] = useState(editing.skillLevelId ?? '');
  const [effectiveOn, setEffectiveOn] = useState(today);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const held = editing.skillLevelId && editing.skillLevelName && !active.some((row) => row.id === editing.skillLevelId) ? [{ value: editing.skillLevelId, label: editing.skillLevelName }] : [];
  const options = [{ value: '', label: t('org.noLevel') }, ...held, ...active.map((row) => ({ value: row.id, label: row.name }))];
  const unchanged = (skillLevelId || null) === editing.skillLevelId;

  const save = async () => {
    setBusy(true);
    const answer = await post(ORG_SET_LEVEL_PATH, { employmentId: editing.employmentId, skillLevelId: skillLevelId || null, effectiveOn, reason: reason.trim() }, apiBaseUrl);
    setBusy(false);
    if (!answer.ok) return setError(answer.message);
    onSaved();
  };

  return (
    <Modal
      open
      title={t('employment.level.title')}
      subtitle={editing.label}
      dataModal="set-level"
      onCancel={onCancel}
      actions={[
        { kind: 'primary', label: t('modal.save'), onAct: () => void save(), disabled: busy || unchanged || reason.trim() === '' || !effectiveOn },
        { kind: 'cancel', label: t('modal.cancel'), onAct: onCancel },
      ]}
    >
      {error ? <AlertBanner tone="error" message={error} action={{ label: t('modal.dismiss'), onAction: () => setError(null) }} /> : null}
      {levels.state.kind === 'failed' ? <p className={modalStyles.fieldLabel} data-set-level-error="true">{levels.state.error}</p> : null}
      <p className={modalStyles.fieldLabel}>{t('employment.level.hint')}</p>
      <Select size={Size.Medium} label={t('org.level')} value={skillLevelId} onChange={setSkillLevelId} options={options} dataAttribute={{ 'data-set-level-level': 'true' }} />
      <DatePicker size={Size.Small} label={t('org.effectiveDate')} value={effectiveOn} onChange={setEffectiveOn} max={today} required name="setLevelEffectiveOn" />
      <TextInput size={Size.Large} label={t('org.reason')} value={reason} onChange={setReason} required name="setLevelReason" />
      <p className={modalStyles.fieldLabel}>{t('org.pastDateNote')}</p>
    </Modal>
  );
}
